import React, { useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Box, Chip, Fab } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import dayjs from "dayjs"

import { createEnterprise, showEnterprise } from "../../../actions/Organizations/Enterprise"
import { getOrganizationsEnterprisesSelector } from "../../../reducers/Organizations/organizations"
import EnterpriseFormModal from "./form/EnterpriseFormModal"
import DataGrid from "../../../components/DataGrid/DataGrid"
import { TableDateFormat } from "../../../components/table-utils/utils"
import EnterprisesHeader from "./EnterprisesHeader"

const ENTERPRISE_STATUS_OPTIONS = [
  { key: "ACTIVE", value: "Actif", label: "Actif" },
  { key: "ARCHIVED", value: "Archivé", label: "Archivé" },
]

const sx = {
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  fab: {
    position: "fixed",
    bottom: 32,
    marginLeft: "32px",
    width: "fit-content"
  },
  chipActive: {
    backgroundColor: "#C9E5D0",
    color: "#2D6B3F",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: 400,
    height: 32,
  },
  chipArchived: {
    backgroundColor: "#DEDEDE",
    color: "#555555",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: 400,
    height: 32,
  },
}

const columns = [
  {
    key: "name",
    label: "Nom de l'entreprise",
    dataType: "text",
    sortable: true,
    filter: {
      filterable: true,
      type: "text",
      fullText: true
    },
    width: "25%"
  },
  {
    key: "code",
    label: "Code",
    dataType: "text",
    sortable: false,
    filter: {
      filterable: true,
      type: "text",
      fullText: true
    },
    width: "25%"
  },
  {
    key: "createdAt",
    label: "Date de création",
    dataType: "date",
    sortable: true,
    filter: {
      filterable: true,
      type: "dateRange",
      labelFrom: "De",
      labelTo: "À"
    },
    width: "25%"
  },
  {
    key: "status",
    label: "Statut",
    dataType: "text",
    sortable: false,
    filter: {
      filterable: true,
      type: "select",
      multiple: true,
      options: ENTERPRISE_STATUS_OPTIONS
    },
    render: (status) => (
      <Chip
        label={status}
        sx={status === "Actif" ? sx.chipActive : sx.chipArchived}
      />
    ),
    width: "25%"
  },
]

const Enterprises = () => {
  const dispatch = useDispatch()
  const enterprises = useSelector(getOrganizationsEnterprisesSelector)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [serverErrors, setServerErrors] = useState(null)

  const data = useMemo(() => enterprises.map((enterprise) => ({
    id: enterprise.objectId,
    name: enterprise.name,
    code: enterprise.code,
    createdAt: dayjs(enterprise.createdAt).format(TableDateFormat),
    status: enterprise.status === "ARCHIVED" ? "Archivé" : "Actif",
  })), [enterprises])

  const handleRowClick = (row) => dispatch(showEnterprise(row.id))

  const handleOpenModal = () => {
    setServerErrors(null)
    setIsModalOpen(true)
  }

  const handleSubmit = async (values) => {
    const result = await dispatch(createEnterprise(values))
    if (result?.codeAlreadyExists) {
      setServerErrors(prev => ({ ...prev, code: "Ce code est déjà utilisé par une autre entreprise." }))
      return
    }
    setIsModalOpen(false)
  }

  return (
    <Box sx={sx.page}>
      <EnterprisesHeader />
      <DataGrid
        title="Paramètres des entreprises"
        columns={columns}
        data={data}
        withFilters
        rowLabel="entreprises"
        onRowClick={handleRowClick}
        withLeftAndRightEmptyColumns={false}
        containerPaperElevation={0}
        sxBodyCell={{ borderBottom: "1px solid #E0E0E0" }}
        initialValues={{ status: ["Actif"] }}
      />
      <Box sx={sx.fab}>
        <Fab color="primary" onClick={handleOpenModal}>
          <AddIcon />
        </Fab>
      </Box>

      <EnterpriseFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        serverErrors={serverErrors}
      />
    </Box>
  )
}

export default Enterprises
